import React, { useState } from 'react';
import { X, Clock, Save, History, MessageSquarePlus, CheckCircle2, Circle, RefreshCw } from 'lucide-react';
import * as api from '../api';

const formatRelative = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min}min atrás`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h atrás`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d atrás`;
  return d.toLocaleDateString('pt-BR');
};

const activityColor = (field) => {
  const f = String(field || '').toLowerCase();
  if (f === 'progresso') return 'bg-blue-500';
  if (f === 'note' || f === 'nota') return 'bg-amber-500';
  if (f === 'status' || f === 'status_change') return 'bg-emerald-500';
  if (f === 'equipe' || f === 'equipe_change') return 'bg-purple-500';
  return 'bg-slate-500';
};

export default function TaskProgressModal({ task, currentUser, onClose, onUpdate }) {
  const [progress, setProgress] = useState(Number(task.progress || 0));
  const [note, setNote] = useState('');
  const [eventMessage, setEventMessage] = useState('');
  const [savingProgress, setSavingProgress] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [savingSla, setSavingSla] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSaveSla = async (raw) => {
    const v = Math.max(1, Math.min(365, Number(raw) || 5));
    if (v === Number(task.slaDays || 5)) return;
    setSavingSla(true);
    setError(''); setSuccess('');
    try {
      const data = await api.setTaskSla(task.id, v);
      onUpdate && onUpdate(data.task);
      setSuccess(`SLA atualizado para ${v} dias.`);
      setTimeout(() => setSuccess(''), 2500);
    } catch (e) {
      setError(e.message || 'Falha ao atualizar SLA');
    } finally {
      setSavingSla(false);
    }
  };

  if (!task) return null;

  const activityLog = Array.isArray(task.activityLog) ? task.activityLog : [];

  const handleSaveProgress = async () => {
    if (savingProgress) return;
    const p = Math.max(0, Math.min(100, Number(progress) || 0));
    if (p === Number(task.progress || 0) && !note.trim()) {
      setError('Nenhuma alteração a salvar.');
      return;
    }
    setSavingProgress(true);
    setError('');
    setSuccess('');
    try {
      const data = await api.updateTaskProgress(task.id, p, note.trim());
      onUpdate && onUpdate(data.task);
      setNote('');
      setSuccess(`Progresso atualizado para ${p}%.`);
      setTimeout(() => setSuccess(''), 2500);
    } catch (e) {
      setError(e.message || 'Falha ao salvar.');
    } finally {
      setSavingProgress(false);
    }
  };

  const handleAddEvent = async () => {
    if (savingEvent) return;
    const msg = eventMessage.trim();
    if (!msg) {
      setError('Digite uma mensagem para o evento.');
      return;
    }
    setSavingEvent(true);
    setError('');
    setSuccess('');
    try {
      const data = await api.addTaskActivity(task.id, 'nota', msg);
      onUpdate && onUpdate(data.task);
      setEventMessage('');
      setSuccess('Evento adicionado à timeline.');
      setTimeout(() => setSuccess(''), 2500);
    } catch (e) {
      setError(e.message || 'Falha ao adicionar evento.');
    } finally {
      setSavingEvent(false);
    }
  };

  const handleQuickSet = (value) => {
    setProgress(value);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-[#181b26] border border-blue-500/40 rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <CheckCircle2 size={20} className="text-blue-300" />
              Progresso e timeline
            </h3>
            <p className="text-sm opacity-70 mt-0.5">{task.elemento || task.name || `Tarefa ${task.id}`}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-sm flex items-center gap-2"><Circle size={14} className="text-blue-300" /> Progresso atual: <span className="text-blue-300 font-bold">{Number(task.progress || 0)}%</span></h4>
              <div className="text-xs opacity-60">SLA: {Number(task.slaDays || 5)} dias</div>
            </div>
            <div className="h-3 bg-slate-900 rounded-full overflow-hidden mb-3">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${Number(task.progress || 0)}%` }} />
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {[0, 25, 50, 75, 100].map((v) => (
                <button
                  key={v}
                  onClick={() => handleQuickSet(v)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${progress === v ? 'bg-blue-600 border-blue-400 text-white' : 'border-slate-700 text-slate-300 hover:bg-slate-700'}`}
                >{v}%</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[120px]">
                <label className="text-xs opacity-70 block mb-1">Novo progresso</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={progress}
                  onChange={(e) => setProgress(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs opacity-70 block mb-1">Nota (opcional)</label>
                <input
                  type="text"
                  maxLength={200}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ex: fotos externas prontas"
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white"
                />
              </div>
              <button onClick={handleSaveProgress} disabled={savingProgress} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-blue-900 text-white text-sm font-semibold flex items-center gap-2">
                <Save size={14} />
                {savingProgress ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </section>

          <section>
            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><Clock size={14} className="text-emerald-300" /> SLA desta tarefa</h4>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs opacity-70 block mb-1">Limite em dias (1-365)</label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    defaultValue={Number(task.slaDays || 5)}
                    onBlur={(e) => handleSaveSla(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                    className="w-full px-3 py-2 pr-9 rounded-lg bg-slate-900 border border-slate-700 text-white"
                  />
                  {savingSla ? <RefreshCw size={14} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-emerald-300" /> : null}
                </div>
              </div>
              <div className="text-xs opacity-70 flex-1 min-w-[200px]">
                Última atividade: {task.lastActivityAt ? new Date(task.lastActivityAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : 'desconhecida'}
              </div>
            </div>
          </section>

          <section>
            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><MessageSquarePlus size={14} className="text-amber-300" /> Adicionar evento a timeline</h4>
            <div className="flex flex-wrap gap-2 items-end">
              <input
                type="text"
                value={eventMessage}
                onChange={(e) => setEventMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddEvent(); }}
                placeholder="Ex: cliente aprovou a lâmina 3"
                maxLength={500}
                className="flex-1 min-w-[200px] px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white"
              />
              <button onClick={handleAddEvent} disabled={savingEvent} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:bg-amber-900 text-slate-900 text-sm font-semibold flex items-center gap-2">
                <Save size={14} />
                {savingEvent ? 'Salvando...' : 'Adicionar'}
              </button>
            </div>
          </section>

          {error ? <div className="bg-red-500/15 border border-red-500/40 text-red-200 rounded-lg px-3 py-2 text-sm">{error}</div> : null}
          {success ? <div className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 rounded-lg px-3 py-2 text-sm">{success}</div> : null}

          <section>
            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><History size={14} className="text-slate-300" /> Timeline ({activityLog.length} evento{activityLog.length === 1 ? '' : 's'})</h4>
            {activityLog.length === 0 ? (
              <div className="text-sm opacity-60 text-center py-6 bg-slate-900/40 rounded-lg">Nenhum evento registrado ainda.</div>
            ) : (
              <div className="space-y-2">
                {activityLog.map((evt) => (
                  <div key={evt.id} className="flex items-start gap-3 bg-slate-900/40 border border-slate-700 rounded-lg p-3">
                    <div className={`w-8 h-8 rounded-full ${activityColor(evt.field)} flex items-center justify-center text-[10px] font-bold text-white shrink-0`}>
                      {(evt.authorInitials || '?').slice(0, 3)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-sm">
                          <span className="font-semibold">{evt.authorName || evt.authorInitials || 'Sistema'}</span>
                          <span className="opacity-60"> · {evt.field || 'evento'}</span>
                        </div>
                        <div className="text-[10px] opacity-60 flex items-center gap-1">
                          <Clock size={10} />
                          {evt.time || (evt.timestamp ? formatRelative(evt.timestamp) : '')}
                        </div>
                      </div>
                      <div className="text-sm text-slate-200 mt-1 break-words">{evt.details}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="p-4 border-t border-slate-700 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-700 hover:bg-white/10 text-sm">Fechar</button>
        </div>
      </div>
    </div>
  );
}
